<template>
    <div class="autocomplete-input">
        <div class="input-append">
            <input
                autocomplete="off"
                class="form-control" :class="{ 'form-error': isError }" :name="name"
                v-model="keyword"
                @blur="onInput()"
                @keyup.esc="showList=false"
                @keydown.down="moveDown" @keydown.up="moveUp"
                @keydown.enter="onSearch"
                :placeholder="placeholder"
                :required="required"
                :readonly="readonly"
                :ref="name"
            />
            <div v-if="loading">
                <button class="btn btn-default" type="button" tabindex="-1" disabled>
                    <span><div v-if="loading" class="inline-loader" style="width: 20px; height: 20px;"></div></span>
                </button>
            </div>
            <div v-else>
                <button class="btn btn-primary" type="button" @click="onSearch" tabindex="-1">Search</button>
            </div>
        </div>
        <input :autocompleteOld="'force_off_' + Math.random() * 10000 + 1" autocomplete="off" class="hidden" :name="name + '_id'" v-model="keywordId" />
        <ul v-show="showList" class="options-list" ref="list">
            <li v-if="!optionMatches || optionMatches.length === 0" @click="showList=false">No Results</li>
            <li v-else v-for="(option, index) in optionMatches" :key="index" :class="{'highlighted': index === highlightedPosition}"
              z-index="950"
              @keydown.tab="onSelect"
              @keydown.enter="onSelect"
              @mouseleave="highlightedPosition = -1"
              @mouseenter="highlightedPosition = index"
              @mousedown="onSelect">
                <slot name="item" :code="option.code" :itemname="option.name" :item="option"></slot>
            </li>
        </ul>
    </div>
</template>
<script>
import Axios from '../classes/Axios.js';
import 'regenerator-runtime/runtime';
import {defineComponent} from 'vue'
export default defineComponent ({
    name: 'SearchResource',

    emits: ['update:modelValue', 'error', 'select', 'loading', 'message'],

    data() {
        return {
            selected: false,
            selecting: false, // in the process of searching
            showList: false,
            isError: false,
            keyword: this.modelValue,
            keywordId: '',
            debouncedInput: undefined,
            highlightedPosition: this.highlight, // Current list item to highlight
            preselected: undefined,
            mustMatch: this.match, // allows for not forcing a selection
            query: '', // Search sent to Rest Resource API
            resources: [],
            loading: false,
            oasisApi: new Axios('/restv2'),
        }
    },

    props: {
        modelValue: {
            type: String,
            required: false,
            default: ''
        },
        preselect: {
            type: [ String, Number, Object ],
            required: false,
            default: ''
        },
        display: {
            required: false,
            type: String,
            default: 'name'
        },
        secondaryDisplay: {
            required: false,
            type: String,
            default: undefined
        },
        name: {
            type: String,
            required: true
        },

        // Choose to pass in the list of options or an endpoint to search
        options: {
            type: Array,
            required: false
        },
        endpoint: {
            required: false,
            type: String,
        },

        highlight: {
            type: Number,
            required: false,
            default: -1
        },
        match: {
            type: Boolean,
            required: false,
            default: true,
        },
        placeholder: {
            type: String,
            required: false
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false
        },
        searchProps: {
            type: Array,
            required: false,
            default: () => [ 'name', 'code' ]
        },

        // Primary filter to be used with resourceClass, e.g. ?filter=
        primaryFilter: {
            required: false,
            type: String,
            default: undefined
        },

        // Filter options that are not the primary filter.  Helps with limiting options, e.g. { type__in: 'POSTED', 'CLOSED' }
        filterOptions: {
            required: false,
            type: Object,
            default () {
                return {}
            }
        },

        // Number of characters to collect before querying API
        minKeywordLength: {
            required: false,
            type: Number,
            default: 1,
        },
    },

    computed: {
        optionMatches() {
            // Will return matches that start with desired values and append loose matches after.
            // Will also prefer to match names than codes
            // console.log(this.searchProps);
            if (! this.keyword) return [];
            const bestMatch = new RegExp('^' + this.keyword, 'i');
            const looseMatch = new RegExp('(?!^)' + this.keyword, 'i');

            if (this.endpoint && this.resources && this.resources.length) {
                return this.resources;
            } else if (this.endpoint) {
                // not supposed to be here
                // console.log(this.endpoint, 'no resources');
                return [];
            } else {
                let bestMatches = this.options.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(bestMatch)) return (option);
                    }
                }).sort(this.optionComp);

                let looseMatches = this.options.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(looseMatch)) return (option);
                    }
                }).sort(this.optionComp);

                let matches = bestMatches.concat(looseMatches);

                // console.log('matches', matches, matches.filter((match, index, self) => self.findIndex(t => t.id === match.id) === index));
                return matches.filter((match, index, self) => self.findIndex(t => t.id === match.id) === index);
            }
        }
    },

    methods: {
        async onSearch(value) {
            // handle events properly because we have multiple keydown events
            if (this.selecting === true) {
                this.onSelect(value);
                return;
            }

            const searchTerm = (value && typeof value === 'string' ? value : this.keyword) || this.keyword;
            if (!searchTerm) {
                alert('Please enter a search term for ' + this.name);
                return;
            }
            this.selecting = true;
            const results = await this.lookup(searchTerm);
            if (!results || results.length === 0) this.selecting = false;

            this.$refs[this.name].blur();
            this.$refs[this.name].focus();
            this.highlightedPosition = this.highlight;
            this.showList = true;
        },

        // Move the highlighed item index up
        moveUp() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition > 0) {
                this.highlightedPosition--;

            // Else scroll to the bottom
            } else if (this.showList && this.highlightedPosition <= 0) {
                this.highlightedPosition = this.optionMatches.length - 1;
            } else {
                return false;
            }
            this.scrollIntoViewIfNeeded(this.$refs['list-items'][this.highlightedPosition]);
            return true;
        },

        // Move the highlighed item index down
        moveDown() {
            // Only scroll down if not past the last item on the list
            if (this.showList && this.optionMatches.length && this.highlightedPosition < this.optionMatches.length - 1) {
                this.highlightedPosition++;

            // Else scroll to the top
            } else if (this.showList) {
                this.highlightedPosition = 0;
            } else {
                return false;
            }
            this.scrollIntoViewIfNeeded(this.$refs['list-items'][this.highlightedPosition]);
            return true;
        },

        scrollIntoViewIfNeeded (element) {
            // Target is outside the viewport from the bottom
            if (element.getBoundingClientRect().bottom > (parseFloat(window.innerHeight) * 0.95)) {
                //  The bottom of the element will be aligned to the bottom of the visible area of the scrollable ancestor.
                element.scrollIntoView(false);
            }

            // Target is outside the view from the top
            if (element.getBoundingClientRect().top < 160) {
                // The top of the element will be aligned to the top of the visible area of the scrollable ancestor
                element.scrollIntoView(false);
            }
        },
      
        // Send this value back so the v-model can be updated
        onInput() {
            if (this.selecting === false) this.showList = false;
            if (this.keyword) {
                // this.$emit('input', this.keyword);
                this.$emit('update:modelValue', this.keyword)
            }
            return
        },

        // When selecting, store the id and send back the display
        onSelect() {
            let displayedValue;
            let selectedResource;

            if (this.highlightedPosition < 0 && !this.keyword) {
                // console.log('no item');
                this.selecting = false;
                return;
            } else if (this.highlightedPosition >= 0 && this.optionMatches && this.optionMatches[this.highlightedPosition]) {
                selectedResource = this.optionMatches[this.highlightedPosition];
                // console.log('Selected', selectedResource);
            } else if (this.keyword && this.keyword === this.preselected) {
                // console.log('matches preselect. ignoring', this.keyword, this.preselect);
            } else if (this.keyword) {
                // console.log('No selection. keeping input');
                if (this.mustMatch === "false" || this.mustMatch === false) {
                    this.onInput(this.keyword);
                    this.isError = false;
                } else {
                    this.isError = true;
                    this.$emit('error', selectedResource)
                }
                this.selecting = false;
                return;
            }

            if (selectedResource) {
                // console.log(selectedResource)
                this.selected = true;
                this.$emit('select', selectedResource);

                // Displayed value
                this.debouncedInput = selectedResource[this.display]

                if (this.secondaryDisplay) {
                    this.debouncedInput += `: ${
                        selectedResource[this.secondaryDisplay]
                    }`
                }
                displayedValue = this.debouncedInput 
                this.keyword = displayedValue;
                // console.log('displayedValue', this.display, displayedValue);
                this.onInput(this.keyword);

            } else if (this.selected) {
                // Do nothing if already selected and tabbing through.
            } else {
                // console.log('Selected, but no option');
                if (this.mustMatch === "false" || this.mustMatch === false) {
                    this.onInput(this.keyword);
                    this.isError = false;
                } else {
                    this.isError = true;
                    this.$emit('error', selectedResource)
                    this.selecting = false;
                    return false;
                }
            }

            this.showList = false;
            this.selecting = false;
            this.highlightedPosition = -1;
            return;
        },

        reset () {
            this.debouncedInput = undefined;
            this.selected = false;
            this.selecting = false;
            this.showList = false;
            this.isError = false;
            this.keyword = '';
            this.keywordId = '';
            this.highlightedPosition = -1;
            this.resources = [];
            this.mustMatch = this.match;
            this.loading = false;
            this.$emit('loading', false);
        },

        optionComp (a, b) {
            let aFixed, bFixed;

            if (a.name && b.name) {
                aFixed = a.name.toLowerCase();
                bFixed = b.name.toLowerCase();
            } else if (a.code && b.code) {
                aFixed = a.code.toLowerCase();
                bFixed = b.code.toLowerCase();
            } else {
                return a - b;
            }

            if (aFixed > bFixed) return 1;
            else if (aFixed < bFixed) return -1;
            else return 0;
        },

        // Rest resource lookup
        async lookup (searchTerm) {
            // console.log('lookup');
            if (!searchTerm || typeof searchTerm !== 'string') return false;
            this.loading = true
            this.$emit('loading', true);
            // console.log(searchTerm);

            if (searchTerm && this.primaryFilter) {
                let query = Object.assign({}, this.filterOptions);
                query[this.primaryFilter] = searchTerm;
                try {
                    const url = this.endpoint + '?' + queryString;
                    let response = await this.oasisApi.get(url);
                    // console.log(url, response);
                    this.resources = response.results;
                } catch (error) {
                    this.$emit('message', error);
                    return;
                }
            } else if (searchTerm) {
                let query = Object.assign({}, this.filterOptions);
                query.search = searchTerm;
                try {
                    var queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
                    const url = this.endpoint + '?' + queryString;
                    let response = await this.oasisApi.get(url);
                    // console.log(url, response);
                    this.resources = response.data.results;
                } catch (error) {
                    this.$emit('message', error);
                    return;
                }
            } else {
                // console.log('no keyword')
            }

            this.loading = false;
            this.$emit('loading', false);
            return this.resources;
        },
    },

    created() {
        if (!this.options && !this.endpoint) {
            return null;
        }
    },

    mounted() {
        if (!this.options && !this.endpoint) console.log('Improper search resource setup');

        if (!this.keyword && this.preselect) {
            // console.log('preselect ', this.preselect);
            this.keyword = this.preselected = typeof this.preselect === 'object' ? (this.preselect[this.display] || this.preselect.name || this.preselect.value) : this.preselect || '';
        }
    }
});
</script>
<style scoped>
    .options-list {
        min-width: 150px;
        max-width: 500px;
        max-height: 1000%;
        overflow: scroll;
    }
</style>

